/* eslint-disable import/no-named-as-default-member */
/* User sagas */

import { push, replace } from "redux-first-history"
import { all, call, put, select, take, takeLatest } from "redux-saga/effects"

import ErrorsActions from "../../Redux/ErrorsRedux"
import { bindPendingProviderBanner } from "../../Utils/anotherProviderBanner"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { apiGeneratorBase, apiGeneratorDelete } from "../../Utils/saga"
import { createAccountStepPath, onboardingStepPath } from "../../Utils/utils"
import AuthActions, { AuthSelectors } from "../LoginPage/reducer"
import UserActions, { UserTypes as constants, UserSelectors } from "./reducer"
import * as services from "./services"

const actions = {
  getUserInfoActions: {
    request: UserActions.userRequest,
    success: UserActions.userSuccess,
    errors: UserActions.userFailure
  },
  updateUserInfoActions: {
    request: UserActions.userSaveRequest,
    success: UserActions.userSaveSuccess,
    errors: UserActions.userSaveFailure
  },
  updateUserAvatarInfoActions: {
    request: UserActions.userSaveAvatarRequest,
    success: UserActions.userSaveAvatarSuccess,
    errors: UserActions.userSaveAvatarFailure
  },
  changePasswordActions: {
    request: UserActions.changePasswordRequest,
    success: UserActions.changePasswordSuccess,
    errors: UserActions.changePasswordFailure
  },
  sendCodePasswordActions: {
    request: UserActions.sendCodePasswordRequest,
    success: UserActions.sendCodePasswordSuccess,
    errors: UserActions.sendCodePasswordFailure
  },
  confirmCodePasswordActions: {
    request: UserActions.confirmCodePasswordRequest,
    success: UserActions.confirmCodePasswordSuccess,
    errors: UserActions.confirmCodePasswordFailure
  },
  createNewPasswordActions: {
    request: UserActions.createNewPasswordRequest,
    success: UserActions.createNewPasswordSuccess,
    errors: UserActions.createNewPasswordFailure
  },
  setPasswordActions: {
    request: UserActions.setPasswordRequest,
    success: UserActions.setPasswordSuccess,
    errors: UserActions.setPasswordFailure
  },
  changeEmailsActions: {
    request: UserActions.changeEmailsRequest,
    success: UserActions.changeEmailsSuccess,
    errors: UserActions.changeEmailsFailure
  },
  verifyEmailActions: {
    request: UserActions.verifyEmailRequest,
    success: UserActions.verifyEmailSuccess,
    errors: UserActions.verifyEmailFailure
  },
  confirmEmailsActions: {
    request: UserActions.confirmEmailsRequest,
    success: UserActions.confirmEmailsSuccess,
    errors: UserActions.confirmEmailsFailure
  },
  changeLanguageActions: {
    request: UserActions.changeLanguageRequest,
    success: UserActions.changeLanguageSuccess,
    errors: UserActions.changeLanguageFailure
  },
  sendOnboardingFormActions: {
    request: UserActions.sendOnboardingFormRequest,
    success: UserActions.sendOnboardingFormSuccess,
    errors: UserActions.sendOnboardingFormFailure,
    redirect: onboardingStepPath[3]
  },
  createBusinessAccountActions: {
    request: UserActions.createBusinessAccountRequest,
    success: UserActions.createBusinessAccountSuccess,
    errors: UserActions.createBusinessAccountFailure
  },
  updateBusinessAccountActions: {
    request: UserActions.updateBusinessAccountRequest,
    success: UserActions.updateBusinessAccountSuccess,
    errors: UserActions.updateBusinessAccountFailure,
    withoutErrorNotification: false,
    extraAction: [() => UserActions.getBusinessAccountsWithRoleRequest()]
  },
  getBusinessAccountActions: {
    request: UserActions.getBusinessAccountRequest,
    success: UserActions.getBusinessAccountSuccess,
    errors: UserActions.getBusinessAccountFailure
  },
  getAutoRefundLogsActions: {
    request: UserActions.getAutoRefundLogsRequest,
    success: UserActions.getAutoRefundLogsSuccess,
    errors: UserActions.getAutoRefundLogsFailure,
    withoutErrorNotification: true
  },
  getIntegrationLogsActions: {
    request: UserActions.getIntegrationLogsRequest,
    success: UserActions.getIntegrationLogsSuccess,
    errors: UserActions.getIntegrationLogsFailure,
    withoutErrorNotification: true,
  },
  getBusinessAccountsActions: {
    request: UserActions.getBusinessAccountsRequest,
    success: UserActions.getBusinessAccountsSuccess,
    errors: UserActions.getBusinessAccountsFailure
  },
  getBusinessAccountsWithRoleActions: {
    request: UserActions.getBusinessAccountsWithRoleRequest,
    success: UserActions.getBusinessAccountsWithRoleSuccess,
    errors: UserActions.getBusinessAccountsWithRoleFailure
  },
  deleteBusinessAccountActions: {
    request: UserActions.deleteBusinessAccountRequest,
    success: UserActions.deleteBusinessAccountSuccess,
    errors: UserActions.deleteBusinessAccountFailure,
    extraAction: [UserActions.getBusinessAccountsRequest],
    redirect: "/dashboard"
  },
  skipPaymentConnectActions: {
    request: UserActions.skipPaymentConnectRequest,
    success: UserActions.skipPaymentConnectSuccess,
    errors: UserActions.skipPaymentConnectFailure,
    redirect: onboardingStepPath[5]
  },
  getPaymentProcessorsActions: {
    request: UserActions.getPaymentProcessorsRequest,
    success: UserActions.getPaymentProcessorsSuccess,
    errors: UserActions.getPaymentProcessorsFailure
  },
  connectPaymentProcessorActions: {
    request: UserActions.connectPaymentProcessorRequest,
    success: UserActions.connectPaymentProcessorSuccess,
    errors: UserActions.connectPaymentProcessorFailure,
    withoutErrorNotification: true,
    extraAction: [UserActions.userRequest]
  },
  disconnectPaymentProcessorActions: {
    request: UserActions.disconnectPaymentProcessorRequest,
    success: UserActions.disconnectPaymentProcessorSuccess,
    errors: UserActions.disconnectPaymentProcessorFailure
  },
  disconnectPaymentProcessorExternallyActions: {
    request: UserActions.disconnectPaymentProcessorExternallyRequest,
    success: UserActions.disconnectPaymentProcessorExternallySuccess,
    errors: UserActions.disconnectPaymentProcessorExternallyFailure
  },
  adyenUploadFilesActions: {
    request: UserActions.adyenUploadFilesRequest,
    success: UserActions.adyenUploadFilesSuccess,
    errors: UserActions.adyenUploadFilesFailure,
    withoutErrorNotification: true
  },
  createBillingDescriptorActions: {
    request: UserActions.createBillingDescriptorRequest,
    success: UserActions.createBillingDescriptorSuccess,
    errors: UserActions.createBillingDescriptorFailure,
    redirect: "/dashboard"
  },
  validateBillingDescriptorActions: {
    request: UserActions.validateBillingDescriptorRequest,
    success: UserActions.validateBillingDescriptorSuccess,
    errors: UserActions.validateBillingDescriptorFailure
  },
  updateBillingDescriptorStatusActions: {
    request: UserActions.updateBillingDescriptorStatusRequest,
    success: UserActions.updateBillingDescriptorStatusSuccess,
    errors: UserActions.updateBillingDescriptorStatusFailure
  },
  getBillingDescriptorsActions: {
    request: UserActions.getBillingDescriptorsRequest,
    success: UserActions.getBillingDescriptorsSuccess,
    errors: UserActions.getBillingDescriptorsFailure
  },
  getUserCardsActions: {
    request: UserActions.getUsersCardsRequest,
    success: UserActions.getUsersCardsSuccess,
    errors: UserActions.getUsersCardsFailure
  },
  deleteUserCard: {
    request: UserActions.deleteUserCardRequest,
    success: UserActions.deleteUserCardSuccess,
    errors: UserActions.deleteUserCardFailure
  },
  updateDefaultCard: {
    request: UserActions.updateDefaultCardRequest,
    success: UserActions.updateDefaultCardSuccess,
    errors: UserActions.updateDefaultCardFailure
  },
  createCheckoutSessionActions: {
    request: UserActions.createCheckoutSessionRequest,
    success: UserActions.createCheckoutSessionSuccess,
    errors: UserActions.createCheckoutSessionFailure
  },
  attachPaymentActions: {
    request: UserActions.attachPaymentRequest,
    success: UserActions.attachPaymentSuccess,
    errors: UserActions.attachPaymentFailure,
    extraAction: [UserActions.getBusinessAccountsRequest],
    redirect: createAccountStepPath[6]
  },
  getPremiumFeaturesActions: {
    request: UserActions.getPremiumFeaturesRequest,
    success: UserActions.getPremiumFeaturesSuccess,
    errors: UserActions.getPremiumFeaturesFailure
  },
  getEnrollmentsActions: {
    request: UserActions.getEnrollmentsRequest,
    success: UserActions.getEnrollmentsSuccess,
    errors: UserActions.getEnrollmentsFailure
  },
  getAdminEnrollmentsActions: {
    request: UserActions.getAdminEnrollmentsRequest,
    success: UserActions.getAdminEnrollmentsSuccess,
    errors: UserActions.getAdminEnrollmentsFailure,
  },
  getEnrollmentsWithoutDescriptorsActions: {
    request: UserActions.getEnrollmentsWithoutDescriptorsRequest,
    success: UserActions.getEnrollmentsWithoutDescriptorsSuccess,
    errors: UserActions.getEnrollmentsWithoutDescriptorsFailure
  },
  getDescriptorsByEnrollmentActions: {
    request: UserActions.getDescriptorsByEnrollmentRequest,
    success: UserActions.getDescriptorsByEnrollmentSuccess,
    errors: UserActions.getDescriptorsByEnrollmentFailure
  },
  getMoreDescriptorsByEnrollmentActions: {
    request: UserActions.getMoreDescriptorsByEnrollmentRequest,
    success: UserActions.getMoreDescriptorsByEnrollmentSuccess,
    errors: UserActions.getMoreDescriptorsByEnrollmentFailure
  },
  getVisaCodesByEnrollmentActions: {
    request: UserActions.getVisaCodesByEnrollmentRequest,
    success: UserActions.getVisaCodesByEnrollmentSuccess,
    errors: UserActions.getVisaCodesByEnrollmentFailure,
  },
  getMoreVisaCodesByEnrollmentActions: {
    request: UserActions.getMoreVisaCodesByEnrollmentRequest,
    success: UserActions.getMoreVisaCodesByEnrollmentSuccess,
    errors: UserActions.getMoreVisaCodesByEnrollmentFailure,
  },
  getAdminDescriptorsByEnrollmentActions: {
    request: UserActions.getAdminDescriptorsByEnrollmentRequest,
    success: UserActions.getAdminDescriptorsByEnrollmentSuccess,
    errors: UserActions.getAdminDescriptorsByEnrollmentFailure,
  },
  getMoreAdminDescriptorsByEnrollmentActions: {
    request: UserActions.getMoreAdminDescriptorsByEnrollmentRequest,
    success: UserActions.getMoreAdminDescriptorsByEnrollmentSuccess,
    errors: UserActions.getMoreAdminDescriptorsByEnrollmentFailure,
  },
  updateAdminDescriptorStatusActions: {
    request: UserActions.updateAdminDescriptorStatusRequest,
    success: UserActions.updateAdminDescriptorStatusSuccess,
    errors: UserActions.updateAdminDescriptorStatusFailure,
  },
  updateAdminDescriptorTypeActions: {
    request: UserActions.updateAdminDescriptorTypeRequest,
    success: UserActions.updateAdminDescriptorTypeSuccess,
    errors: UserActions.updateAdminDescriptorTypeFailure,
  },
  updateEnrollmentActions: {
    request: UserActions.updateEnrollmentRequest,
    success: UserActions.updateEnrollmentSuccess,
    errors: UserActions.updateEnrollmentFailure
  },
  changeStatusEnrollmentActions: {
    request: UserActions.changeStatusEnrollmentRequest,
    success: UserActions.changeStatusEnrollmentSuccess,
    errors: UserActions.changeStatusEnrollmentFailure
  },
  changeOnboardingStepActions: {
    request: UserActions.changeOnboardingStepRequest,
    success: UserActions.changeOnboardingStepSuccess,
    errors: UserActions.changeOnboardingStepFailure
  },
  requestNewIntegrationActions: {
    request: UserActions.requestNewIntegrationRequest,
    success: UserActions.requestNewIntegrationSuccess,
    errors: UserActions.requestNewIntegrationFailure
  },
  declinePremiumFeaturesActions: {
    request: UserActions.declinePremiumFeaturesRequest,
    success: UserActions.declinePremiumFeaturesSuccess,
    errors: UserActions.declinePremiumFeaturesFailure,
    extraAction: [() => AuthActions.setShowModalPremiumFeatures(false)]
  },
  updateUserPriceActions: {
    request: UserActions.updateUserPriceRequest,
    success: UserActions.updateUserPriceSuccess,
    errors: UserActions.updateUserPriceFailure
  },
  getMaxCreateStepsActions: {
    request: UserActions.getMaxCreateStepsRequest,
    success: UserActions.getMaxCreateStepsSuccess,
    errors: UserActions.getMaxCreateStepsFailure,
    fakeData: { onboarding_steps: 4 }
  },
  getBannerInfoActions: {
    request: UserActions.getBannerInfoRequest,
    success: UserActions.getBannerInfoSuccess,
    errors: UserActions.getBannerInfoFailure
  },
  trackEventActions: {
    request: UserActions.trackEventRequest,
    success: UserActions.trackEventSuccess,
    errors: UserActions.trackEventFailure
  },
  getUserInfoByAdminActions: {
    request: UserActions.getUserInfoByAdminRequest,
    success: UserActions.getUserInfoByAdminSuccess,
    errors: UserActions.getUserInfoByAdminFailure
  },
  getBusinessInfoActions: {
    request: UserActions.getBusinessInfoRequest,
    success: UserActions.getBusinessInfoSuccess,
    errors: UserActions.getBusinessInfoFailure,
    withoutErrorNotification: true
  },
  changeEmailOnboardingActions: {
    request: UserActions.changeEmailOnboardingRequest,
    success: UserActions.changeEmailOnboardingSuccess,
    errors: UserActions.changeEmailOnboardingFailure
  },
  checkMaverickDbaActions: {
    request: UserActions.checkMaverickDbaRequest,
    success: UserActions.checkMaverickDbaSuccess,
    errors: UserActions.checkMaverickDbaFailure,
    withoutErrorNotification: true
  },
  connectMaverickTerminalsActions: {
    request: UserActions.connectMaverickTerminalsRequest,
    success: UserActions.connectMaverickTerminalsSuccess,
    errors: UserActions.connectMaverickTerminalsFailure
  }
}

const eventsOptions = {
  [constants.USER_REQUEST]: {
    api: services.getUserInfo,
    actions: actions.getUserInfoActions
  },
  [constants.USER_SAVE_REQUEST]: {
    api: services.updateUserInfo,
    actions: actions.updateUserInfoActions
  },
  [constants.USER_SAVE_AVATAR_REQUEST]: {
    api: services.updateUserInfo,
    actions: actions.updateUserAvatarInfoActions
  },
  [constants.CHANGE_PASSWORD_REQUEST]: {
    api: services.changePassword,
    actions: actions.changePasswordActions
  },
  [constants.SEND_CODE_PASSWORD_REQUEST]: {
    api: services.sendCodePassword,
    actions: actions.sendCodePasswordActions
  },
  [constants.CONFIRM_CODE_PASSWORD_REQUEST]: {
    api: services.confirmCodePassword,
    actions: actions.confirmCodePasswordActions
  },
  [constants.CREATE_NEW_PASSWORD_REQUEST]: {
    api: services.createNewPassword,
    actions: actions.createNewPasswordActions
  },
  [constants.SET_PASSWORD_REQUEST]: {
    api: services.createNewPassword,
    actions: actions.setPasswordActions
  },
  [constants.CHANGE_EMAILS_REQUEST]: {
    api: services.changeEmails,
    actions: actions.changeEmailsActions
  },
  [constants.VERIFY_EMAIL_REQUEST]: {
    api: services.verifyEmail,
    actions: actions.verifyEmailActions
  },
  [constants.CONFIRM_EMAILS_REQUEST]: {
    api: services.confirmEmails,
    actions: actions.confirmEmailsActions
  },
  [constants.CHANGE_LANGUAGE_REQUEST]: {
    api: services.changeLanguage,
    actions: actions.changeLanguageActions
  },
  [constants.SEND_ONBOARDING_FORM_REQUEST]: {
    api: services.sendOnboardingForm,
    actions: actions.sendOnboardingFormActions
  },
  [constants.CREATE_BUSINESS_ACCOUNT_REQUEST]: {
    api: services.createBusinessAccount,
    actions: actions.createBusinessAccountActions
  },
  [constants.UPDATE_BUSINESS_ACCOUNT_REQUEST]: {
    api: services.updateBusinessAccount,
    actions: actions.updateBusinessAccountActions
  },
  [constants.GET_BUSINESS_ACCOUNT_REQUEST]: {
    api: services.getBusinessAccount,
    actions: actions.getBusinessAccountActions
  },
  [constants.GET_AUTO_REFUND_LOGS_REQUEST]: {
    api: services.getAutoRefundLogs,
    actions: actions.getAutoRefundLogsActions
  },
  [constants.GET_INTEGRATION_LOGS_REQUEST]: {
    api: services.getIntegrationLogs,
    actions: actions.getIntegrationLogsActions,
  },
  [constants.GET_BUSINESS_ACCOUNTS_REQUEST]: {
    api: services.getBusinessAccounts,
    actions: actions.getBusinessAccountsActions
  },
  [constants.GET_BUSINESS_ACCOUNTS_WITH_ROLE_REQUEST]: {
    api: services.getBusinessAccountsWithRole,
    actions: actions.getBusinessAccountsWithRoleActions
  },
  [constants.DELETE_BUSINESS_ACCOUNT_REQUEST]: {
    api: services.deleteBusinessAccount,
    actions: actions.deleteBusinessAccountActions
  },
  [constants.SKIP_PAYMENT_CONNECT_REQUEST]: {
    api: services.skipPaymentConnect,
    actions: actions.skipPaymentConnectActions
  },
  [constants.GET_PAYMENT_PROCESSORS_REQUEST]: {
    api: services.getPaymentProcessors,
    actions: actions.getPaymentProcessorsActions
  },
  [constants.CONNECT_PAYMENT_PROCESSOR_REQUEST]: {
    api: services.connectPaymentProcessor,
    actions: actions.connectPaymentProcessorActions
  },
  [constants.DISCONNECT_PAYMENT_PROCESSOR_REQUEST]: {
    api: services.disconnectPaymentProcessor,
    actions: actions.disconnectPaymentProcessorActions
  },
  [constants.DISCONNECT_PAYMENT_PROCESSOR_EXTERNALLY_REQUEST]: {
    api: services.disconnectPaymentProcessor,
    actions: actions.disconnectPaymentProcessorExternallyActions
  },
  [constants.CREATE_BILLING_DESCRIPTOR_REQUEST]: {
    api: services.createBillingDescriptor,
    actions: actions.createBillingDescriptorActions
  },
  [constants.VALIDATE_BILLING_DESCRIPTOR_REQUEST]: {
    api: services.validateBillingDescriptor,
    actions: actions.validateBillingDescriptorActions
  },
  [constants.UPDATE_BILLING_DESCRIPTOR_STATUS_REQUEST]: {
    api: services.updateBillingDescriptorStatus,
    actions: actions.updateBillingDescriptorStatusActions
  },
  [constants.GET_BILLING_DESCRIPTORS_REQUEST]: {
    api: services.getBillingDescriptors,
    actions: actions.getBillingDescriptorsActions
  },
  [constants.GET_USERS_CARDS_REQUEST]: {
    api: services.getUsersCards,
    actions: actions.getUserCardsActions
  },
  [constants.DELETE_USER_CARD_REQUEST]: {
    api: services.deleteCard,
    actions: actions.deleteUserCard
  },
  [constants.UPDATE_DEFAULT_CARD_REQUEST]: {
    api: services.updateDefaultCard,
    actions: actions.updateDefaultCard
  },
  [constants.CREATE_CHECKOUT_SESSION_REQUEST]: {
    api: services.createCheckoutSession,
    actions: actions.createCheckoutSessionActions
  },
  [constants.ATTACH_PAYMENT_REQUEST]: {
    api: services.attachPayment,
    actions: actions.attachPaymentActions
  },
  [constants.GET_PREMIUM_FEATURES_REQUEST]: {
    api: services.getPremiumFeatures,
    actions: actions.getPremiumFeaturesActions
  },
  [constants.GET_ENROLLMENTS_REQUEST]: {
    api: services.getEnrollments,
    actions: actions.getEnrollmentsActions
  },
  [constants.GET_ADMIN_ENROLLMENTS_REQUEST]: {
    api: services.getAdminEnrollments,
    actions: actions.getAdminEnrollmentsActions,
  },
  [constants.GET_ENROLLMENTS_WITHOUT_DESCRIPTORS_REQUEST]: {
    api: services.getEnrollmentsWithoutDescriptors,
    actions: actions.getEnrollmentsWithoutDescriptorsActions
  },
  [constants.GET_DESCRIPTORS_BY_ENROLLMENT_REQUEST]: {
    api: services.getDescriptorsByEnrollment,
    actions: actions.getDescriptorsByEnrollmentActions
  },
  [constants.GET_MORE_DESCRIPTORS_BY_ENROLLMENT_REQUEST]: {
    api: services.getDescriptorsByEnrollment,
    actions: actions.getMoreDescriptorsByEnrollmentActions
  },
  [constants.GET_VISA_CODES_BY_ENROLLMENT_REQUEST]: {
    api: services.getVisaCodesByEnrollment,
    actions: actions.getVisaCodesByEnrollmentActions,
  },
  [constants.GET_MORE_VISA_CODES_BY_ENROLLMENT_REQUEST]: {
    api: services.getVisaCodesByEnrollment,
    actions: actions.getMoreVisaCodesByEnrollmentActions,
  },
  [constants.GET_ADMIN_DESCRIPTORS_BY_ENROLLMENT_REQUEST]: {
    api: services.getAdminDescriptorsByEnrollment,
    actions: actions.getAdminDescriptorsByEnrollmentActions,
  },
  [constants.GET_MORE_ADMIN_DESCRIPTORS_BY_ENROLLMENT_REQUEST]: {
    api: services.getAdminDescriptorsByEnrollment,
    actions: actions.getMoreAdminDescriptorsByEnrollmentActions,
  },
  [constants.UPDATE_ADMIN_DESCRIPTOR_STATUS_REQUEST]: {
    api: services.updateAdminDescriptorStatus,
    actions: actions.updateAdminDescriptorStatusActions,
  },
  [constants.UPDATE_ADMIN_DESCRIPTOR_TYPE_REQUEST]: {
    api: services.updateAdminDescriptorType,
    actions: actions.updateAdminDescriptorTypeActions,
  },
  [constants.UPDATE_ENROLLMENT_REQUEST]: {
    api: services.updateEnrollment,
    actions: actions.updateEnrollmentActions
  },
  [constants.CHANGE_STATUS_ENROLLMENT_REQUEST]: {
    api: services.changeStatusEnrollment,
    actions: actions.changeStatusEnrollmentActions
  },
  [constants.CHANGE_ONBOARDING_STEP_REQUEST]: {
    api: services.updateUserInfo,
    actions: actions.changeOnboardingStepActions
  },
  [constants.REQUEST_NEW_INTEGRATION_REQUEST]: {
    api: services.requestNewIntegration,
    actions: actions.requestNewIntegrationActions
  },
  [constants.DECLINE_PREMIUM_FEATURES_REQUEST]: {
    api: services.declinePremiumFeatures,
    actions: actions.declinePremiumFeaturesActions
  },
  [constants.UPDATE_USER_PRICE_REQUEST]: {
    api: services.updateUserPrice,
    actions: actions.updateUserPriceActions
  },
  [constants.GET_MAX_CREATE_STEPS_REQUEST]: {
    api: services.getMaxCreateSteps,
    actions: actions.getMaxCreateStepsActions
  },
  [constants.GET_BANNER_INFO_REQUEST]: {
    api: services.getBannerInfo,
    actions: actions.getBannerInfoActions
  },
  [constants.TRACK_EVENT_REQUEST]: {
    api: services.trackEvent,
    actions: actions.trackEventActions
  },
  [constants.GET_USER_INFO_BY_ADMIN_REQUEST]: {
    api: services.getUserInfoByAdmin,
    actions: actions.getUserInfoByAdminActions
  },
  [constants.GET_BUSINESS_INFO_REQUEST]: {
    api: services.getBusinessInfo,
    actions: actions.getBusinessInfoActions
  },
  [constants.CHANGE_EMAIL_ONBOARDING_REQUEST]: {
    api: services.changeEmailOnboarding,
    actions: actions.changeEmailOnboardingActions
  },
  [constants.ADYEN_UPLOAD_FILES_REQUEST]: {
    api: services.adyenUploadFiles,
    actions: actions.adyenUploadFilesActions
  },
  [constants.CHECK_MAVERICK_DBA_REQUEST]: {
    api: services.checkMaverickDba,
    actions: actions.checkMaverickDbaActions
  },
  [constants.CONNECT_MAVERICK_TERMINALS_REQUEST]: {
    api: services.connectMaverickTerminals,
    actions: actions.connectMaverickTerminalsActions
  }
}

function* apiGenerator(action) {
  const provider = eventsOptions[action.type]
  const userInfo = yield select(AuthSelectors.getUserInfo)
  const nextCreateAccountStep = yield select(UserSelectors.getNextCreateAccountStep)

  try {
    const params = action.payload
    const response = yield call(provider.api, params)
    if (response.data) {
      if (response.data?.message && response.ok) {
        yield put(provider.actions.success(response?.data?.message))
      } else if (response.data?.message && !response.ok) {
        if (action.type === constants.CREATE_BUSINESS_ACCOUNT_REQUEST) {
          yield put(ErrorsActions.errorSave(response.data.message))
        }
        yield put(provider.actions.errors({ errors: response.data?.message }))
      } else if (response.data && !response.ok) {
        const error = Object.values(response.data).length ? Object.values(response.data)[0] : null
        yield put(provider.actions.errors({ errors: error }))
      } else {
        if (params?.user_id && action.type === constants.USER_SAVE_REQUEST) {
          yield put(provider.actions.success({ data: response.data, user_id: true })) //update by admin
        } else {
          yield put(provider.actions.success(response.data))
        }
        if (action.type === constants.USER_SAVE_REQUEST) {
          yield put(UserActions.userRequest())
        }
        if (action.type === constants.CREATE_BUSINESS_ACCOUNT_REQUEST) {
          bindPendingProviderBanner(response?.data?.id)
          if (!userInfo?.["is_created_first_business_account"] && !nextCreateAccountStep) {
            yield put(
              AuthActions.updateProfile({
                business_account: { ...response?.data },
                is_created_first_business_account: true,
                current_business_account_id: response?.data?.id,
                next_step: 4
              })
            )
            yield call(services.updateCompletedStep, {
              step: 1,
              business_account_id: response?.data?.id
            })
            yield put(replace(onboardingStepPath[4]))
          } else {
            yield put(UserActions.changeNextCreateAccountStep(2))
            yield call(services.updateCompletedStep, {
              step: 1,
              business_account_id: response?.data?.id
            })
            yield put(push(createAccountStepPath[2]))
          }
        } else {
          if (params?.user_id) {
            return //if updated by admin we not update profile
          } else {
            yield put(AuthActions.updateProfile({ ...response?.data }))
          }
        }
        /* yield put(AuthActions.updateProfile({
          ...response?.data,
          is_created_first_business_account: true
        }));*/
      }
    } else {
      yield put(provider.actions.errors({ errors: "some error" }))
    }
  } catch (errors) {
    yield put(provider.actions.errors({ errors }))
  }
}
function* apiSetPassword(action) {
  const provider = eventsOptions[action.type]

  try {
    const params = action.payload
    const response = yield call(provider.api, params)
    if (response.data) {
      if (response.ok) {
        yield put(provider.actions.success(response.data))
        yield put(UserActions.userRequest())
      } else {
        const error = Object.values(response.data).length ? Object.values(response.data)[0] : null
        yield put(provider.actions.errors({ errors: error }))
      }
    } else {
      yield put(provider.actions.errors({ errors: "some error" }))
    }
  } catch (errors) {
    yield put(provider.actions.errors({ errors }))
  }
}

export function* apiCreateSession(action) {
  const provider = eventsOptions[action.type]
  try {
    const params = action.payload
    const response = yield call(provider.api, params)
    if (response.ok && response.data) {
      yield put(provider.actions.success(response.data))
      if (response?.data?.["checkout_session"]?.url)
        window.location.href = response.data["checkout_session"].url
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

export function* apiAttachPayment(action) {
  const provider = eventsOptions[action.type]
  const userInfo = yield select(AuthSelectors.getUserInfo)
  const redirectAfterCreateAccount = yield select(UserSelectors.getRedirectAfterCreateAccount)
  try {
    const params = action.payload
    const response = yield call(provider.api, params)

    if (response.ok && response.data) {
      const redirectUrl = response.data?.is_light_mode
        ? redirectAfterCreateAccount
        : provider.actions.redirect
      if (!isOnboardingFinished(userInfo) && response.data?.onboarding_finished) {
        window.dataLayer = window?.dataLayer || []
        window.dataLayer.push({
          event: "account_created",
          email: response.data.email
        })
        //make request for check if need to push event in window.datalayer
        yield put(UserActions.trackEventRequest({ event_type: "fifth_step" }))
      }
      yield call(services.updateCompletedStep, {
        step: 5,
        business_account_id: response.data?.current_business_account_id
      })
      yield put(UserActions.userSaveRequest({ next_step: 8 }))
      yield put(AuthActions.updateProfile(response.data))
      if (provider.actions?.extraAction)
        yield all(provider.actions?.extraAction.map((action) => put(action())))
      yield put(replace(redirectUrl))
      yield put(provider.actions.success())
      // TODO: will return when have new demo
      // yield put(UserActions.setDemoTourStep(1))
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

export function* apiGeneratorDisconnectPaymentProcessorExternally(action) {
  const provider = eventsOptions[action.type]
  try {
    const params = action.payload
    const response = yield call(provider.api, params)

    if (response.ok && response.data) {
      yield put(AuthActions.updateProfile(response.data))
      yield put(provider.actions.success())
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

export function* apiGeneratorNextStep(action) {
  const provider = eventsOptions[action.type]
  const userInfo = yield select(AuthSelectors.getUserInfo)
  try {
    const params = action.payload
    const response = yield call(provider.api, params)
    if (response.ok && response.data) {
      yield put(provider.actions.success())
      yield put(AuthActions.updateProfile(response.data))
      if (!userInfo?.is_created_by_invitation && onboardingStepPath[params["next_step"]]) {
        yield put(replace(onboardingStepPath[params["next_step"]]))
      }
      if (userInfo?.is_created_by_invitation && createAccountStepPath[params["next_step"] - 2]) {
        yield put(replace(createAccountStepPath[params["next_step"] - 2]))
      }
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

export function* apiCreateBillingDescriptor(action) {
  const nextCreateAccountStep = yield select(UserSelectors.getNextCreateAccountStep)
  const nextManageEnrollmentStep = yield select(UserSelectors.getNextManageEnrollmentStep)
  const maxCreateSteps = yield select(UserSelectors.getMaxCreateSteps)
  const redirectAfterCreateAccount = yield select(UserSelectors.getRedirectAfterCreateAccount)

  const provider = eventsOptions[action.type]

  try {
    const params = action.payload

    const enrollmentId = params?.enrollmentId || null
    const enrollmentInfo = yield select(UserSelectors.getEnrollmentInfo, enrollmentId)
    const isAdmin = yield select((state) => state?.auth?.isAdmin)
    const userInfo = yield select(AuthSelectors.getUserInfo)
    const business_account_id =
      params?.["descriptor_data"]?.[0]?.["account_id"] || params?.[0]?.["account_id"]

    const response = yield call(provider.api, params)
    if (response.ok && response.data) {
      yield put(provider.actions.success(response.data))
      if (nextCreateAccountStep) {
        yield call(services.updateCompletedStep, {
          step: 4,
          business_account_id: business_account_id
        })
        if (maxCreateSteps === 4) {
          const responseFinalize = yield call(services.finalizeOnboarding)
          if (responseFinalize.ok && responseFinalize.data) {
            yield put(AuthActions.updateProfile(responseFinalize.data))
            yield put(UserActions.getBusinessAccountsRequest())
            yield put(replace(redirectAfterCreateAccount || provider.actions.redirect))
            yield put(UserActions.attachPaymentSuccess())
            yield put(UserActions.setDemoTourStep(1))
            // createBillingDescriptorSuccess (User/reducer.js) bumps nextCreateAccountStep from
            // 4 to 5 instead of clearing it, so it stays truthy forever after this - every later,
            // unrelated billing descriptor creation for this already-onboarded account re-enters
            // this same `if (nextCreateAccountStep)` branch, re-running finalizeOnboarding and
            // re-showing the "Business account created" popup. Clear it once finalize actually
            // succeeds so this whole block only ever runs for the real, one-time completion.
            yield put(UserActions.userSetProp("nextCreateAccountStep", null))
          }
        } else {
          yield put(replace(createAccountStepPath[5]))
          if (userInfo?.is_created_by_invitation && !isOnboardingFinished(userInfo)) {
            yield put(AuthActions.updateProfile({ next_step: 7 }))
            yield put(UserActions.userSaveRequest({ next_step: 7 }))
          }
        }
      } else if (nextManageEnrollmentStep) {
        if (enrollmentInfo?.name && !enrollmentInfo?.["is_active"]) {
          yield put(UserActions.userSetProp("saveResult", { add_enrollment: enrollmentInfo?.name }))
        }
        yield put(UserActions.userSetProp("nextManageEnrollmentStep", null))
        yield put(replace(isAdmin ? "/business_settings/enrollment" : "/configure/enrollment"))
      } else {
        yield call(services.updateCompletedStep, {
          step: 4,
          business_account_id: business_account_id
        })
        yield put(UserActions.changeOnboardingStepRequest({ next_step: 7 }))
        yield put(replace(onboardingStepPath[7]))
      }
    } else {
      if (response?.data?.message?.descriptors) {
        yield put(provider.actions.errors(response?.data?.message))
        yield put(ErrorsActions.errorSave(response?.data?.error))
      } else if (response?.data?.message?.caid_codes) {
        yield put(provider.actions.errors(response?.data?.message))
        yield put(ErrorsActions.errorSave(response?.data?.error))
      } else if (typeof response?.data?.error === "string") {
        yield put(ErrorsActions.errorSave(response?.data?.error))
      } else {
        yield put(provider.actions.errors())
        yield put(
          ErrorsActions.errorSave(
            response.status === 500 ? "Server response error" : response?.data
          )
        )
      }
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

export function* apiValidateBillingDescriptor(action) {
  const provider = eventsOptions[action.type]

  try {
    const body = action.payload

    const response = yield call(provider.api, body)

    if (response.ok && response.data) {
      yield put(provider.actions.success(response.data?.validated))
    } else if (response?.data?.message?.descriptors) {
      yield put(provider.actions.errors(response?.data?.message))
      yield put(ErrorsActions.errorSave(response?.data?.error))
    } else if (response?.data?.message?.bin_codes) {
      yield put(provider.actions.errors(response?.data?.message))
      yield put(ErrorsActions.errorSave(response?.data?.error))
    } else if (response?.data?.message?.caid_codes) {
      yield put(provider.actions.errors(response?.data?.message))
      yield put(ErrorsActions.errorSave(response?.data?.error))
    } else if (typeof response?.data?.error === "string") {
      yield put(provider.actions.errors())
      yield put(ErrorsActions.errorSave(response?.data?.error))
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response?.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

export function* apiTrackEvent(action) {
  const provider = eventsOptions[action.type]
  const userInfo = yield select(AuthSelectors.getUserInfo)
  try {
    const params = action.payload
    const { ...requestParams } = params
    const response = yield call(provider.api, requestParams)
    if (response.ok && response.data) {
      yield put(provider.actions.success())
      if (window.dataLayer && response.data?.new_event) {
        window.dataLayer.push({
          event: requestParams?.event_type,
          user_id: userInfo?.id
        })
      }
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

function* changeEmailOnboardingFlow(action) {
  const provider = eventsOptions[action.type]
  const params = action.payload
  try {
    const response = yield call(provider.api, params)

    if (!response?.ok) {
      const errors =
        response?.data?.message?.detail ||
        response?.data?.message ||
        response?.data?.detail ||
        response?.data?.new_email?.[0] ||
        response?.data?.new_email ||
        response?.data

      yield put(UserActions.changeEmailOnboardingFailure({ errors }))
      return
    }

    yield put(UserActions.userRequest())

    const resultAction = yield take([constants.USER_SUCCESS, constants.USER_FAILURE])

    if (resultAction.type === constants.USER_SUCCESS) {
      yield put(UserActions.changeEmailOnboardingSuccess())
    } else {
      yield put(UserActions.changeEmailOnboardingFailure({ errors: "User refresh failed" }))
    }
  } catch (errors) {
    yield put(provider.actions.errors({ errors }))
  }
}

function* checkMaverickDbaSaga(action) {
  try {
    const response = yield call(services.checkMaverickDba, action.payload)

    if (response.ok && response.data?.status === "ok") {
      yield put(UserActions.checkMaverickDbaSuccess(response.data))
    } else {
      yield put(
        UserActions.checkMaverickDbaFailure({
          errors: response.data?.terminals_error || "DBA not valid"
        })
      )
    }
  } catch (e) {
    yield put(UserActions.checkMaverickDbaFailure({ errors: e }))
  }
}
function* connectMaverickTerminalsSaga(action) {
  const provider = eventsOptions[action.type]
  try {
    const params = action.payload
    const response = yield call(provider.api, params)

    if (response.ok && response.data?.is_connected) {
      yield put(AuthActions.updateProfile(response.data))
      yield put(provider.actions.success())
    } else {
      yield put(provider.actions.errors())
      yield put(
        ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
      )
    }
  } catch (errors) {
    yield put(provider.actions.errors())
    yield put(ErrorsActions.errorSave({ errors }))
  }
}

function* getUserInfoSaga() {
  const provider = eventsOptions[constants.USER_REQUEST]
  try {
    const response = yield call(provider.api)
    if (response.ok && response.data) {
      yield put(provider.actions.success(response.data))
      yield put(AuthActions.updateProfile(response.data))
    } else {
      yield put(
        provider.actions.errors({ errors: response.data?.message || "Failed to fetch user data" })
      )
    }
  } catch (error) {
    yield put(provider.actions.errors({ errors: "Failed to fetch user data" }))
  }
}

function* toggleIsDemoDataShownSaga(action) {
  try {
    const { isShown } = action.payload
    const res = yield call(services.updateDemoDataVisibility, { is_show_demo_data: isShown })
    if (res.ok) {
      yield getUserInfoSaga()
      yield put(UserActions.demoDataToggleSuccess(isShown))
    } else {
      yield put(UserActions.demoDataToggleFailure())
    }
  } catch (e) {
    yield put(UserActions.demoDataToggleFailure())
  }
}

function* changeBusinessAccountSaga(action) {
  const { onSuccess, onError, businessAccountId } = action.payload
  try {
    const response = yield call(services.updateUserInfo, {
      current_business_account_id: businessAccountId
    })
    if (response.ok && response.data) {
      yield getUserInfoSaga()
      yield put(UserActions.changeBusinessAccountSuccess(response.data))
      if (onSuccess && typeof onSuccess === "function") onSuccess()
    } else {
      yield put(
        UserActions.changeBusinessAccountFailure({
          errors: response.data?.message || "Failed to change business account"
        })
      )
      if (onError && typeof onError === "function") {
        onError("Failed to change business account")
      }
    }
  } catch (error) {
    yield put(
      UserActions.changeBusinessAccountFailure({
        errors: "Failed to change business account"
      })
    )
    if (onError && typeof onError === "function") {
      onError("Failed to change business account")
    }
  }
}

export default function* apiSaga() {
  yield takeLatest(constants.USER_REQUEST, apiGenerator);
  yield takeLatest(constants.USER_SAVE_REQUEST, apiGenerator);
  yield takeLatest(constants.USER_SAVE_AVATAR_REQUEST, apiGenerator);
  yield takeLatest(constants.CHANGE_PASSWORD_REQUEST, apiGenerator);
  yield takeLatest(constants.SEND_CODE_PASSWORD_REQUEST, apiGenerator);
  yield takeLatest(constants.CONFIRM_CODE_PASSWORD_REQUEST, apiGenerator);
  yield takeLatest(constants.CREATE_NEW_PASSWORD_REQUEST, apiGenerator);
  yield takeLatest(constants.SET_PASSWORD_REQUEST, apiSetPassword);
  yield takeLatest(constants.CHANGE_EMAILS_REQUEST, apiGenerator);
  yield takeLatest(constants.VERIFY_EMAIL_REQUEST, apiGenerator);
  yield takeLatest(constants.CONFIRM_EMAILS_REQUEST, apiGenerator);
  yield takeLatest(constants.CHANGE_LANGUAGE_REQUEST, apiGenerator);
  yield takeLatest(constants.GET_USERS_CARDS_REQUEST, apiGenerator);
  yield takeLatest(constants.UPDATE_DEFAULT_CARD_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_DEFAULT_CARD_REQUEST]))
  yield takeLatest(constants.SEND_ONBOARDING_FORM_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.SEND_ONBOARDING_FORM_REQUEST]));
  yield takeLatest(constants.CREATE_BUSINESS_ACCOUNT_REQUEST, apiGenerator);
  yield takeLatest(constants.GET_BUSINESS_ACCOUNT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_BUSINESS_ACCOUNT_REQUEST]));
  yield takeLatest(constants.GET_AUTO_REFUND_LOGS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_AUTO_REFUND_LOGS_REQUEST]));
  yield takeLatest(constants.GET_INTEGRATION_LOGS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_INTEGRATION_LOGS_REQUEST]));
  yield takeLatest(constants.GET_BUSINESS_ACCOUNTS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_BUSINESS_ACCOUNTS_REQUEST]));
  yield takeLatest(constants.GET_BUSINESS_ACCOUNTS_WITH_ROLE_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_BUSINESS_ACCOUNTS_WITH_ROLE_REQUEST]));
  yield takeLatest(constants.DELETE_BUSINESS_ACCOUNT_REQUEST, (action)=> apiGeneratorDelete(action, eventsOptions[constants.DELETE_BUSINESS_ACCOUNT_REQUEST]));
  yield takeLatest(constants.DELETE_USER_CARD_REQUEST, (action)=> apiGeneratorDelete(action, eventsOptions[constants.DELETE_USER_CARD_REQUEST]));
  yield takeLatest(constants.SKIP_PAYMENT_CONNECT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.SKIP_PAYMENT_CONNECT_REQUEST]));
  yield takeLatest(constants.GET_PAYMENT_PROCESSORS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_PAYMENT_PROCESSORS_REQUEST]));
  yield takeLatest(constants.CONNECT_PAYMENT_PROCESSOR_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.CONNECT_PAYMENT_PROCESSOR_REQUEST]));
  yield takeLatest(constants.DISCONNECT_PAYMENT_PROCESSOR_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.DISCONNECT_PAYMENT_PROCESSOR_REQUEST]));
  yield takeLatest(constants.DISCONNECT_PAYMENT_PROCESSOR_EXTERNALLY_REQUEST, apiGeneratorDisconnectPaymentProcessorExternally);
  yield takeLatest(constants.ADYEN_UPLOAD_FILES_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.ADYEN_UPLOAD_FILES_REQUEST]));
  yield takeLatest(constants.UPDATE_BUSINESS_ACCOUNT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_BUSINESS_ACCOUNT_REQUEST]));
  yield takeLatest(constants.CREATE_BILLING_DESCRIPTOR_REQUEST, apiCreateBillingDescriptor);
  yield takeLatest(constants.VALIDATE_BILLING_DESCRIPTOR_REQUEST, apiValidateBillingDescriptor);
  yield takeLatest(constants.UPDATE_BILLING_DESCRIPTOR_STATUS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_BILLING_DESCRIPTOR_STATUS_REQUEST]));
  yield takeLatest(constants.GET_BILLING_DESCRIPTORS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_BILLING_DESCRIPTORS_REQUEST]));
  yield takeLatest(constants.CREATE_CHECKOUT_SESSION_REQUEST, apiCreateSession);
  yield takeLatest(constants.ATTACH_PAYMENT_REQUEST, apiAttachPayment);
  yield takeLatest(constants.GET_PREMIUM_FEATURES_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_PREMIUM_FEATURES_REQUEST]));
  yield takeLatest(constants.GET_ENROLLMENTS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_ENROLLMENTS_REQUEST]));
  yield takeLatest(constants.GET_ADMIN_ENROLLMENTS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_ADMIN_ENROLLMENTS_REQUEST]));
  yield takeLatest(constants.GET_ENROLLMENTS_WITHOUT_DESCRIPTORS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_ENROLLMENTS_WITHOUT_DESCRIPTORS_REQUEST]));
  yield takeLatest(constants.GET_DESCRIPTORS_BY_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_DESCRIPTORS_BY_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.GET_MORE_DESCRIPTORS_BY_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_MORE_DESCRIPTORS_BY_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.GET_VISA_CODES_BY_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_VISA_CODES_BY_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.GET_MORE_VISA_CODES_BY_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_MORE_VISA_CODES_BY_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.GET_ADMIN_DESCRIPTORS_BY_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_ADMIN_DESCRIPTORS_BY_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.GET_MORE_ADMIN_DESCRIPTORS_BY_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_MORE_ADMIN_DESCRIPTORS_BY_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.UPDATE_ADMIN_DESCRIPTOR_STATUS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_ADMIN_DESCRIPTOR_STATUS_REQUEST]));
  yield takeLatest(constants.UPDATE_ADMIN_DESCRIPTOR_TYPE_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_ADMIN_DESCRIPTOR_TYPE_REQUEST]));
  yield takeLatest(constants.UPDATE_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.CHANGE_STATUS_ENROLLMENT_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.CHANGE_STATUS_ENROLLMENT_REQUEST]));
  yield takeLatest(constants.REQUEST_NEW_INTEGRATION_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.REQUEST_NEW_INTEGRATION_REQUEST]));
  yield takeLatest(constants.CHANGE_ONBOARDING_STEP_REQUEST, apiGeneratorNextStep);
  yield takeLatest(constants.DECLINE_PREMIUM_FEATURES_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.DECLINE_PREMIUM_FEATURES_REQUEST]));
  yield takeLatest(constants.UPDATE_USER_PRICE_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.UPDATE_USER_PRICE_REQUEST]));
  yield takeLatest(constants.GET_MAX_CREATE_STEPS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_MAX_CREATE_STEPS_REQUEST]));
  yield takeLatest(constants.GET_BANNER_INFO_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_BANNER_INFO_REQUEST]));
  yield takeLatest(constants.TRACK_EVENT_REQUEST, apiTrackEvent);
  yield takeLatest(constants.GET_USER_INFO_BY_ADMIN_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_USER_INFO_BY_ADMIN_REQUEST]));
  yield takeLatest(constants.GET_BUSINESS_INFO_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.GET_BUSINESS_INFO_REQUEST]));
  yield takeLatest(constants.CHANGE_EMAIL_ONBOARDING_REQUEST, changeEmailOnboardingFlow);
  yield takeLatest(constants.CHECK_MAVERICK_DBA_REQUEST, checkMaverickDbaSaga);
  yield takeLatest(constants.CONNECT_MAVERICK_TERMINALS_REQUEST, (action)=> apiGeneratorBase(action, eventsOptions[constants.CONNECT_MAVERICK_TERMINALS_REQUEST]));
  yield takeLatest(constants.DEMO_DATA_TOGGLE_REQUEST, toggleIsDemoDataShownSaga);
  yield takeLatest(constants.CHANGE_BUSINESS_ACCOUNT_REQUEST, changeBusinessAccountSaga);
}
